/**
 * The canonical Deal Model composer row model, plus lossless converters
 * between it and the stored source document.
 *
 * The `auction-hub-deal-model-source-v0.2` document is
 * the stored, hashed, versioned artifact. The row model is an in-memory
 * projection shared by the grid, the text DSL and the outcomes panel; nothing
 * is ever stored as DSL text.
 *
 * Money is GBP decimal everywhere the composer writes.
 */

import { singleLine } from './scanner'
import type { EngineScope, EngineScopeResolver, FactPathBinder, FactPathOfKey } from './expressionPaths'
import {
  engineScopeResolver,
  expressionToEnginePaths,
  expressionToRowKeys,
  factPathsInExpression,
} from './expressionPaths'

/* ------------------------------------------------------------------ types */

/** `money` is GBP decimal, 2dp; `moneyPence` is integral pence. */
export const SEMANTIC_TYPES = [
  'money',
  'ratio',
  'percentage',
  'integer',
  'decimal',
  'count',
  'months',
  'days',
  'areaSqm',
  'boolean',
  'enum',
  'string',
  'date',
  'moneyPence',
] as const

export type SemanticType = (typeof SEMANTIC_TYPES)[number]

export function isSemanticType(value: unknown): value is SemanticType {
  return typeof value === 'string' && (SEMANTIC_TYPES as readonly string[]).includes(value)
}

export function isNumericSemanticType(value: SemanticType): boolean {
  return value === 'money' || value === 'moneyPence' || value === 'ratio' || value === 'percentage'
    || value === 'integer' || value === 'decimal' || value === 'count' || value === 'months'
    || value === 'days' || value === 'areaSqm'
}

export interface RowFields {
  id: string
  key: string
  label: string
  semanticType: SemanticType
  unit?: string
  note?: string
}

/** A catalogue-bound input: reads a canonical fact. */
export interface SystemRow extends RowFields { kind: 'system'; factPath: string }
/**
 * An overridable value with a default, authored as `input <type> <name> =
 * <literal>` (wave 5b §8).
 *
 * A null marker prints as `input <type> <name> = null`: a required project
 * input with no stored default, resolved only from an explicit override.
 * Other defaults are what the model computes with until someone changes it, and
 * the document marks the row as overridable per property and per user
 * (`resolution.propertyOverrideKey` / `userSettingKey`). A bare literal with no
 * `input` keyword is NOT one of these: it is a calc row holding a constant,
 * which nobody can override.
 */
export interface InputRow extends RowFields { kind: 'input'; defaultValue: string | number | boolean | null }
/** A ZEN expression row. */
export interface CalcRow extends RowFields { kind: 'calc'; expressionZen: string }

export interface OutcomeTier {
  value: string
  /** null marks the default tier; only the last tier may be a default. */
  conditionZen: string | null
}

/**
 * The compiler owns `not_computable`: it is the arm emitted before any tier
 * when a required input is missing. No authored tier may claim it.
 */
export const NOT_COMPUTABLE_TIER_VALUE = 'not_computable'

/** Tier values the composer refuses to author. */
export const RESERVED_TIER_VALUES: readonly string[] = [NOT_COMPUTABLE_TIER_VALUE]

export function isReservedTierValue(value: string): boolean {
  return RESERVED_TIER_VALUES.includes(value)
}

/**
 * 1–4 tiers TOTAL, the default arm included.
 *
 * Wave 5b §10 raised the ceiling from three to four so the owner's vocabulary
 * fits: **good** (every hurdle met), **average** (positive, below the hurdle),
 * **poor** (around break-even, worth a closer look) and **kill** (costs exceed
 * the sale). The vocabulary itself is the built-ins' convention, not a schema
 * enum — a model may name its four tiers whatever it likes.
 */
export const MAX_OUTCOME_TIERS = 4

/** A Deal Model exposes at most two card values. */
export const MAX_CARD_OUTCOMES = 2

/** A hard-bound outcome: one main verdict, up to two card values. */
export interface OutcomeRow extends RowFields {
  kind: 'outcome'
  role: 'main' | 'card'
  /** main only; ordered; first match wins; the last may be a default. */
  tiers?: OutcomeTier[]
  /** card only; the key of the calc row it exposes. */
  sourceRowKey?: string
}

/**
 * A standalone `# …` comment line.
 *
 * The source document stores these in `composerAnnotations`, so they survive a
 * save and reload.
 */
export interface CommentRow { kind: 'comment'; id: string; text: string }
/** A blank spacing line, persisted the same way a comment is. */
export interface BlankRow { kind: 'blank'; id: string }
/** A line that could not be parsed, preserved verbatim so nothing is lost. */
export interface ErrorRow { kind: 'error'; id: string; raw: string; message: string }

export type SemanticRow = SystemRow | InputRow | CalcRow | OutcomeRow
export type TextRow = CommentRow | BlankRow | ErrorRow
export type Row = SemanticRow | TextRow
export type RowKind = Row['kind']

export interface Group { id: string; label: string; rowIds: string[] }

/** rows in document order. */
export interface ComposerModel { rows: Row[]; groups: Group[] }

export function isSemanticRow(row: Row): row is SemanticRow {
  return row.kind === 'system' || row.kind === 'input' || row.kind === 'calc' || row.kind === 'outcome'
}

export function isValueRow(row: Row): row is SystemRow | InputRow | CalcRow {
  return row.kind === 'system' || row.kind === 'input' || row.kind === 'calc'
}

export function emptyModel(): ComposerModel {
  return { rows: [], groups: [] }
}

/** Which engine scope each row key occupies, for the expression rewrite. */
export function modelScopeResolver(model: ComposerModel): EngineScopeResolver {
  const bindings: Array<readonly [string, EngineScope]> = []
  for (const row of model.rows) {
    if (row.kind === 'system' || row.kind === 'input') bindings.push([row.key, 'inputs'])
    else if (row.kind === 'calc') bindings.push([row.key, 'outputs'])
  }
  return engineScopeResolver(bindings)
}

/* -------------------------------------------------- implicit fact bindings */

/**
 * Wave 5b §1: a catalogue fact path may be used **straight inside a formula**
 * (`money totalCost = listing.marketedPricePence + sdlt`) with no alias row in
 * front of it. The value semantics are identical to an explicit binding — a
 * pence fact still arrives in pounds, because the ÷100 lives in the compiler's
 * fact binding either way.
 *
 * The document has no shape for a bare path, so each distinct implicit path is
 * stored as an ordinary `inputs[]` entry under a deterministic key. That key is
 * the whole convention: an input whose key is exactly `implicitKeyForPath(its
 * own factPath)` is an implicit binding, so nothing extra has to be recorded on
 * the document and no schema change is needed.
 */
export const IMPLICIT_BINDING_KEY_PREFIX = 'sys_'

/**
 * The deterministic input key an implicitly referenced fact path is stored under.
 *
 * Wave 5d §1: a literal `_` in a path segment becomes `_0` before the
 * separators become `_`, so `listing.a_b` (→ `sys_listing_a_0b`) and
 * `listing.a.b` (→ `sys_listing_a_b`) stay apart AND the result is still a
 * storable key. Wave 5c escaped the underscore as `__`, which the v0.2 key
 * pattern ({@link DOCUMENT_KEY_PATTERN}) forbids — every real underscored
 * catalogue path (`area.crimeRatePer1000_12m`, `operating.ground_rent`,
 * `works.base_refurbishment`) became unstorable rather than merely ambiguous.
 *
 * The encoding is not injective for every conceivable path — `listing.a_b` and
 * `listing.a.0b` both encode to `sys_listing_a_0b` — so the writer still checks
 * for a residual collision and reports it as a DSL diagnostic that names a row
 * (see {@link implicitBindingKeyCollisions}), instead of letting the compiler
 * answer with an unfixable DUPLICATE_SOURCE_KEY that points at no line. Every
 * emitted key is also validated against the schema pattern at write time.
 */
export function implicitKeyForPath(factPath: string): string {
  return `${IMPLICIT_BINDING_KEY_PREFIX}${factPath.replace(/_/gu, '_0').replace(/\./gu, '_')}`
}

/** A distinct fact path that encodes to a key another path already claimed. */
export interface ImplicitKeyCollision {
  key: string
  /** Every path that encodes to `key`, in first-appearance order. */
  factPaths: string[]
  /** The first row that references the losing path — what the author can fix. */
  rowId: string
}

/**
 * Residual `sys_` key collisions in this model, if any.
 *
 * Two distinct paths that encode to one key would become one `inputs[]` entry
 * (or a duplicate one), so the model cannot be stored faithfully. This is
 * reported against a real row so the author sees the underline on the formula
 * that names the second path.
 */
export function implicitBindingKeyCollisions(model: ComposerModel): ImplicitKeyCollision[] {
  const byKey = new Map<string, ImplicitBinding[]>()
  for (const binding of implicitBindings(model)) {
    const existing = byKey.get(binding.key)
    if (existing) existing.push(binding)
    else byKey.set(binding.key, [binding])
  }
  const collisions: ImplicitKeyCollision[] = []
  for (const [key, bindings] of byKey) {
    if (bindings.length < 2) continue
    const loser = bindings[1]!
    collisions.push({
      key,
      factPaths: bindings.map((binding) => binding.factPath),
      rowId: loser.rowIds[0] ?? bindings[0]!.rowIds[0] ?? '',
    })
  }
  return collisions
}

/**
 * Is this the synthesised entry for an implicitly referenced fact, rather than
 * a row the author wrote?
 *
 * The test is exact — the key must be this very path's encoding — because a
 * `sys_` key alone is ambiguous: a path segment may itself contain `_`, so the
 * key cannot be decoded back into a path. The stored `factPath` is the truth
 * and the key is only a name derived from it.
 */
export function isImplicitBindingKey(key: string, factPath: string): boolean {
  return key === implicitKeyForPath(factPath)
}

/** True for a system row that stands for an implicit reference, not an alias. */
export function isImplicitBindingRow(row: Row): row is SystemRow {
  return row.kind === 'system' && isImplicitBindingKey(row.key, row.factPath)
}

/** A catalogue fact a formula references without aliasing it to a row first. */
export interface ImplicitBinding {
  /** The path exactly as the author wrote it, e.g. `listing.marketedPricePence`. */
  factPath: string
  /** The document input key it is stored under: `sys_listing_marketedPricePence`. */
  key: string
  /** Inferred from the path; the catalogue may know better and the UI may say so. */
  semanticType: SemanticType
  /** `pence` for a `*Pence` fact — the compiler divides by 100 at binding. */
  factUnit: string
  /** The rows whose expression or tier condition references it, in model order. */
  rowIds: string[]
}

/** The type an unaliased fact path takes when only its spelling is known. */
export function semanticTypeForFactPath(factPath: string): SemanticType {
  return /pence$/iu.test(factPath) ? 'money' : 'decimal'
}

/** The unit a bound fact arrives in, from its path alone. */
export function factUnitForPath(factPath: string, semanticType: SemanticType): string {
  if (/Pence$/u.test(factPath)) return 'pence'
  return semanticType === 'money' ? 'pounds' : 'native'
}

/** Every name the model declares — the guard against reading `row.field` as a fact. */
export function modelRowKeys(model: ComposerModel): Set<string> {
  const keys = new Set<string>()
  for (const row of model.rows) if (isValueRow(row)) keys.add(row.key)
  return keys
}

/**
 * The fact paths this model references implicitly, in first-appearance order.
 *
 * A path the author HAS aliased (`money purchasePrice = listing.marketedPrice…`)
 * is not listed: the alias row takes over, and a formula mentioning that path
 * binds to the alias instead of minting a second entry for the same fact. The
 * composer uses this for the Preview inputs panel, the "used" markers in the
 * variables rail and the tooltip on a path token.
 */
export function implicitBindings(model: ComposerModel): ImplicitBinding[] {
  const rowKeys = modelRowKeys(model)
  const aliased = new Set<string>()
  for (const row of model.rows) if (row.kind === 'system') aliased.add(row.factPath)
  // `deal.<key>` is the main outcome's own output path, never a fact to bind.
  const mainOutcomePath = mainOutcomeOutputPath(model)

  const found = new Map<string, ImplicitBinding>()
  const collect = (expression: string | null | undefined, rowId: string): void => {
    if (expression === null || expression === undefined || expression.length === 0) return
    for (const factPath of factPathsInExpression(expression, rowKeys)) {
      if (aliased.has(factPath) || factPath === mainOutcomePath) continue
      const existing = found.get(factPath)
      if (existing) {
        if (!existing.rowIds.includes(rowId)) existing.rowIds.push(rowId)
        continue
      }
      const semanticType = semanticTypeForFactPath(factPath)
      found.set(factPath, {
        factPath,
        key: implicitKeyForPath(factPath),
        semanticType,
        factUnit: factUnitForPath(factPath, semanticType),
        rowIds: [rowId],
      })
    }
  }
  for (const row of model.rows) {
    if (row.kind === 'calc') collect(row.expressionZen, row.id)
    else if (row.kind === 'outcome' && row.role === 'main') {
      for (const tier of row.tiers ?? []) collect(tier.conditionZen, row.id)
    }
  }
  return [...found.values()]
}

function mainOutcomeOutputPath(model: ComposerModel): string | null {
  for (const row of model.rows) {
    if (row.kind === 'outcome' && row.role === 'main') return `deal.${row.key}`
  }
  return null
}

/* ---------------------------------------------------------- json helpers */

export type JsonRecord = Record<string, unknown>

function asRecord(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && !isLosslessNumber(value)
    ? (value as JsonRecord)
    : null
}

function asRecordArray(value: unknown): JsonRecord[] {
  if (!Array.isArray(value)) return []
  const out: JsonRecord[] = []
  for (const item of value) {
    const record = asRecord(item)
    if (record) out.push(record)
  }
  return out
}

function isLosslessNumber(value: unknown): value is { isLosslessNumber: boolean; toString(): string } {
  return typeof value === 'object' && value !== null && (value as { isLosslessNumber?: unknown }).isLosslessNumber === true
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

/** Read a JSON number that may have arrived as a lossless-json token. */
export function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'bigint') return Number(value)
  if (isLosslessNumber(value)) {
    const parsed = Number(value.toString())
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u
/**
 * The v0.2 schema's key pattern, verbatim (`$defs.key`). Kept here so the DSL
 * layer can prove a key it is about to write is storable BEFORE the document
 * reaches the server — wave 5d §1.
 */
export const DOCUMENT_KEY_PATTERN = /^[a-z][a-z0-9]*(?:[A-Z][A-Za-z0-9]*|[._-][a-z0-9]+)*$/u
const DOCUMENT_PATH_PATTERN = /^[a-z][A-Za-z0-9]*(?:\.[a-z][A-Za-z0-9]*)+$/u

export function isDocumentUuid(value: string): boolean { return UUID_PATTERN.test(value) }
export function isDocumentKey(value: string): boolean { return value.length <= 100 && DOCUMENT_KEY_PATTERN.test(value) }
export function isDocumentPath(value: string): boolean { return DOCUMENT_PATH_PATTERN.test(value) }

/* ------------------------------------------------------------ conversion */

export const CONTRACT_VERSION_V02 = 'auction-hub-deal-model-source-v0.2'

export type DocumentVersion = 'v0.2'

export interface ConversionWarning {
  code: string
  message: string
  rowId?: string
  pointer?: string
  /** `error` = the document cannot be stored as written; defaults to a warning. */
  severity?: 'error' | 'warning'
}

export interface DocumentToModelResult {
  model: ComposerModel
  version: DocumentVersion
  warnings: ConversionWarning[]
}

/* ------------------------------------------------------ composerAnnotations */

/**
 * The optional v0.2 array that lets a comment or a blank line survive a save.
 *
 * The compiler ignores it entirely — it is authoring furniture, not model
 * meaning — but without it the text view loses every standalone `#` note the
 * moment the draft is reloaded, which is the one thing that made the DSL feel
 * lossy. `afterRowId` names the document id of the row the annotation follows;
 * `null` puts it before the first row, and the array is read and written in
 * document order.
 */
export const COMPOSER_ANNOTATIONS_KEY = 'composerAnnotations'

/** The contract's own ceiling on the array. */
export const MAX_COMPOSER_ANNOTATIONS = 200

export interface ComposerAnnotation {
  id: string
  kind: 'comment' | 'blank'
  afterRowId: string | null
  text?: string
}

/** True when this document already declares the array, empty or not. */
export function documentCarriesComposerAnnotations(baseDocument: unknown): boolean {
  const base = asRecord(baseDocument)
  return base !== null && Array.isArray(base[COMPOSER_ANNOTATIONS_KEY])
}

function readComposerAnnotations(source: JsonRecord): ComposerAnnotation[] {
  const annotations: ComposerAnnotation[] = []
  const raw = source[COMPOSER_ANNOTATIONS_KEY]
  if (!Array.isArray(raw)) return annotations
  raw.forEach((entry, index) => {
    const record = asRecord(entry)
    if (!record) return
    const kind = asString(record.kind)
    if (kind !== 'comment' && kind !== 'blank') return
    const afterRowId = asString(record.afterRowId)
    const text = asString(record.text)
    annotations.push({
      id: asString(record.id) ?? `annotation:${index + 1}`,
      kind,
      afterRowId,
      ...(kind === 'comment' ? { text: text === null ? '' : singleLine(text) } : {}),
    })
  })
  return annotations
}

/**
 * Weave stored annotations back between the rows they were authored against.
 *
 * Document order is preserved within one anchor, so two comments written one
 * after another come back in that order rather than reversed.
 */
function applyComposerAnnotations(
  rows: Row[],
  annotations: ComposerAnnotation[],
  warnings: ConversionWarning[],
): Row[] {
  if (annotations.length === 0) return rows
  const known = new Set(rows.map((row) => row.id))
  const leading: Row[] = []
  const following = new Map<string, Row[]>()
  for (const annotation of annotations) {
    const row: Row = annotation.kind === 'comment'
      ? { kind: 'comment', id: annotation.id, text: annotation.text ?? '' }
      : { kind: 'blank', id: annotation.id }
    if (annotation.afterRowId === null) { leading.push(row); continue }
    if (!known.has(annotation.afterRowId)) {
      warnings.push({
        code: 'annotationAnchorUnresolved',
        message: `A stored composer annotation follows ${annotation.afterRowId}, which is no longer a row; it is shown at the end.`,
        pointer: `/${COMPOSER_ANNOTATIONS_KEY}`,
      })
      continue
    }
    const bucket = following.get(annotation.afterRowId)
    if (bucket) bucket.push(row)
    else following.set(annotation.afterRowId, [row])
  }
  const out: Row[] = [...leading]
  for (const row of rows) {
    out.push(row)
    const bucket = following.get(row.id)
    if (bucket) out.push(...bucket)
  }
  return out
}

/**
 * Project the model's comment and blank rows back onto the document.
 *
 * `documentIds` is the model-id → document-id map the row emission built, so an
 * annotation anchored to a row whose document identity was just minted still
 * points at the id the document actually stores.
 */
function collectComposerAnnotations(
  model: ComposerModel,
  documentIds: Map<string, string>,
  newId: () => string,
): ComposerAnnotation[] {
  const annotations: ComposerAnnotation[] = []
  let anchor: string | null = null
  for (const row of model.rows) {
    if (isSemanticRow(row)) {
      // Only an input, a calc row or a block instance is a legal anchor, so an
      // annotation written between two outcomes stays with the last real row
      // rather than naming an id the document does not resolve.
      if (isValueRow(row)) anchor = documentIds.get(row.id) ?? (isDocumentUuid(row.id) ? row.id : anchor)
      continue
    }
    // An unparseable line is not an annotation: it is the author's own text and
    // the text view keeps it verbatim, but the document has no shape for it.
    if (row.kind !== 'comment' && row.kind !== 'blank') continue
    annotations.push({
      id: isDocumentUuid(row.id) ? row.id : newId(),
      kind: row.kind,
      afterRowId: anchor,
      ...(row.kind === 'comment' ? { text: singleLine(row.text) } : {}),
    })
  }
  return annotations
}

function documentVersionOf(source: JsonRecord): DocumentVersion {
  const contractVersion = asString(source.contractVersion) ?? ''
  if (contractVersion !== CONTRACT_VERSION_V02) throw new TypeError(`The Deal Model source contract must be ${CONTRACT_VERSION_V02}`)
  return 'v0.2'
}

function readSemanticType(value: unknown, fallback: SemanticType): SemanticType {
  if (isSemanticType(value)) return value
  const record = asRecord(value)
  const kind = record ? record.kind : undefined
  return isSemanticType(kind) ? kind : fallback
}

function readUnit(record: JsonRecord): string | undefined {
  const unit = asString(record.unit)
  return unit === null ? undefined : unit
}

function readNote(record: JsonRecord): string | undefined {
  const description = asString(record.description)
  if (description === null) return undefined
  const normalized = singleLine(description)
  return normalized.length > 0 ? normalized : undefined
}

function readLiteral(value: unknown, semanticType: SemanticType): string | number | boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    if (isNumericSemanticType(semanticType)) {
      const parsed = Number(value)
      if (value.trim().length > 0 && Number.isFinite(parsed)) return parsed
    }
    return value
  }
  const numeric = asNumber(value)
  if (numeric !== null) return numeric
  return null
}

function defaultForSemanticType(semanticType: SemanticType): string | number | boolean {
  if (semanticType === 'boolean') return false
  if (semanticType === 'string' || semanticType === 'enum' || semanticType === 'date') return ''
  return 0
}

function optional<T extends object>(base: T, extras: Record<string, unknown>): T {
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) }
  for (const [key, value] of Object.entries(extras)) if (value !== undefined) out[key] = value
  return out as T
}

/** Convert a current stored source document into the composer row model. */
export function documentToModel(sourceDocument: unknown): ComposerModel {
  return convertDocumentToModel(sourceDocument).model
}

export function convertDocumentToModel(sourceDocument: unknown): DocumentToModelResult {
  const source = asRecord(sourceDocument)
  if (!source) throw new TypeError('The Deal Model source document must be a JSON object')
  const version = documentVersionOf(source)
  const warnings: ConversionWarning[] = []

  const inputRows: SemanticRow[] = []
  /** Implicit bindings: stored as inputs, shown as paths, never rows. */
  const implicitPathOfKey = new Map<string, string>()
  asRecordArray(source.inputs).forEach((input, index) => {
    const key = asString(input.key) ?? `input${index + 1}`
    const id = asString(input.id) ?? `input:${key}`
    const label = asString(input.label) ?? key
    const semanticType = readSemanticType(input.semanticType, 'decimal')
    const resolution = asRecord(input.resolution)
    const factPath = resolution ? asString(resolution.factPath) : null
    const shared = { id, key, label, semanticType }
    const extras = { unit: readUnit(input), note: readNote(input) }
    if (factPath !== null) {
      // The synthesised entry for a fact a formula names directly is not a row
      // of the sheet: every reference to it reads back as the path itself, and
      // the writer mints the entry again from those references.
      if (isImplicitBindingKey(key, factPath)) {
        implicitPathOfKey.set(key, factPath)
        return
      }
      inputRows.push(optional<SystemRow>({ kind: 'system', ...shared, factPath }, extras))
      return
    }
    const rawDefault = 'defaultValue' in input ? input.defaultValue : undefined
    let defaultValue = rawDefault === undefined ? null : readLiteral(rawDefault, semanticType)
    const requiredProjectInput = rawDefault === undefined
      && input.requiredForComputation === true && asString(resolution?.propertyOverrideKey) !== null
    if (defaultValue === null && !requiredProjectInput) {
      defaultValue = defaultForSemanticType(semanticType)
      warnings.push({
        code: 'defaultValueSynthesised',
        message: `Input ${key} has no usable default; the composer shows ${JSON.stringify(defaultValue)}.`,
        rowId: id,
        pointer: `/inputs/${index}`,
      })
    }
    inputRows.push(optional<InputRow>({ kind: 'input', ...shared, defaultValue }, extras))
  })

  const calcRows: CalcRow[] = []
  const calcById = new Map<string, CalcRow>()
  asRecordArray(source.rows).forEach((row, index) => {
    const key = asString(row.key) ?? `row${index + 1}`
    const id = asString(row.id) ?? `row:${key}`
    const calc = optional<CalcRow>({
      kind: 'calc',
      id,
      key,
      label: asString(row.label) ?? key,
      semanticType: readSemanticType(row.semanticType, 'decimal'),
      expressionZen: typeof row.expressionZen === 'string' ? row.expressionZen : '',
    }, { unit: readUnit(row), note: readNote(row) })
    calcRows.push(calc)
    calcById.set(id, calc)
  })

  // The document stores engine paths; the composer edits bare row keys, and an
  // implicit binding reads back as the fact path the author actually wrote.
  const scopeOf = engineScopeResolver([
    ...inputRows.map((row) => [row.key, 'inputs'] as const),
    ...calcRows.map((row) => [row.key, 'outputs'] as const),
  ])
  const factPathOfKey = (key: string): string | null => implicitPathOfKey.get(key) ?? null
  for (const calc of calcRows) calc.expressionZen = expressionToRowKeys(calc.expressionZen, scopeOf, factPathOfKey)

  // Ungrouped inputs precede the grouped values. Inputs that a source places
  // in a group must stay alongside that group's calculations, otherwise the
  // text prints the same heading twice and reads it as two different groups.
  const groups: Group[] = []
  const placedCalcIds = new Set<string>()
  const placedInputIds = new Set<string>()
  const inputById = new Map(inputRows.map(row => [row.id, row]))
  const orderedValueRows: SemanticRow[] = []
  asRecordArray(source.groups).forEach((group, index) => {
    const groupId = asString(group.id) ?? `group:${index + 1}`
    const rowIds: string[] = []
    for (const item of asRecordArray(group.items)) {
      const itemId = asString(item.id)
      const itemKind = asString(item.kind)
      if (itemId === null) continue
      if (itemKind === 'row') {
        const calc = calcById.get(itemId)
        if (!calc || placedCalcIds.has(itemId)) continue
        placedCalcIds.add(itemId)
        orderedValueRows.push(calc)
        rowIds.push(itemId)
      } else if (itemKind === 'input') {
        const input = inputById.get(itemId)
        if (!input || placedInputIds.has(itemId)) continue
        placedInputIds.add(itemId)
        orderedValueRows.push(input)
        rowIds.push(itemId)
      }
    }
    groups.push({ id: groupId, label: asString(group.label) ?? asString(group.key) ?? `Group ${index + 1}`, rowIds })
  })
  for (const calc of calcRows) if (!placedCalcIds.has(calc.id)) orderedValueRows.push(calc)

  const outcomeRows: OutcomeRow[] = []
  const outcomes = asRecord(source.outcomes)
  if (outcomes) {
    const main = asRecord(outcomes.main)
    if (main) {
      const key = asString(main.key) ?? 'verdict'
      outcomeRows.push({
        kind: 'outcome',
        id: asString(main.rowId) ?? 'outcome:main',
        key,
        label: asString(main.label) ?? 'Verdict',
        semanticType: readSemanticType(main.semanticType, 'enum'),
        role: 'main',
        tiers: readTiers(outcomes.main, warnings, scopeOf, factPathOfKey),
      })
    }
    for (const entry of asRecordArray(outcomes.card)) {
      const referenced = calcById.get(asString(entry.rowId) ?? '')
      const key = asString(entry.key) ?? referenced?.key ?? 'cardValue'
      outcomeRows.push({
        kind: 'outcome',
        id: `outcome:card:${key}`,
        key,
        label: asString(entry.label) ?? referenced?.label ?? key,
        semanticType: readSemanticType(entry.semanticType, referenced?.semanticType ?? 'decimal'),
        role: 'card',
        sourceRowKey: referenced?.key ?? key,
      })
    }
  }

  // Comments and blank lines are authoring furniture the compiler ignores, but
  // they are the author's own words: a v0.2 document that stores them gets them
  // back in place rather than losing them on reload.
  const rows = applyComposerAnnotations(
    [...inputRows.filter(row => !placedInputIds.has(row.id)), ...orderedValueRows, ...outcomeRows],
    readComposerAnnotations(source),
    warnings,
  )

  return { model: { rows, groups }, version, warnings }
}

function readTiers(value: unknown, warnings: ConversionWarning[], scopeOf: EngineScopeResolver, factPathOfKey: FactPathOfKey): OutcomeTier[] {
  const main = asRecord(value)
  const tiers: OutcomeTier[] = []
  if (!main) return tiers
  for (const tier of asRecordArray(main.tiers)) {
    const tierValue = asString(tier.value)
    if (tierValue === null) continue
    if (isReservedTierValue(tierValue)) {
      warnings.push({
        code: 'reservedTierValue',
        message: `${tierValue} is reserved for the compiler and cannot be an authored tier value.`,
        pointer: '/outcomes/main/tiers',
      })
    }
    const conditionZen = asString(tier.conditionZen)
    tiers.push({ value: tierValue, conditionZen: conditionZen === null ? null : expressionToRowKeys(conditionZen, scopeOf, factPathOfKey) })
  }
  if (tiers.length > MAX_OUTCOME_TIERS) {
    warnings.push({ code: 'tierCountExceeded', message: `The main outcome declares ${tiers.length} tiers; the composer supports up to ${MAX_OUTCOME_TIERS}.`, pointer: '/outcomes/main/tiers' })
  }
  return tiers
}

/* ------------------------------------------------------- model → document */

export interface ModelToDocumentOptions {
  /** Emit `{ kind: 'input', id }` group items. */
  inputGroupItems?: boolean
  /**
   * Emit the optional v0.2 `composerAnnotations` array so comments and blank
   * lines survive a save. Defaults to on when the base document already carries
   * one; pass `true` once the schema declares the field, `false` never to write
   * it.
   */
  composerAnnotations?: boolean
  /** Identifier factory for rows that have no document-shaped UUID yet. */
  newId?: () => string
}

export interface ModelToDocumentResult {
  document: JsonRecord
  version: DocumentVersion
  warnings: ConversionWarning[]
}

function randomUuid(): string {
  const generator = globalThis.crypto as { randomUUID?: () => string } | undefined
  if (generator?.randomUUID) return generator.randomUUID()
  throw new Error('A crypto.randomUUID implementation is required to mint Deal Model identifiers')
}

function displayFormat(semanticType: SemanticType): string {
  if (semanticType === 'money' || semanticType === 'moneyPence') return 'money'
  if (semanticType === 'percentage' || semanticType === 'ratio') return 'percentage'
  if (semanticType === 'integer' || semanticType === 'count' || semanticType === 'months' || semanticType === 'days') return 'integer'
  if (semanticType === 'boolean') return 'boolean'
  if (semanticType === 'enum') return 'enum'
  if (semanticType === 'date') return 'date'
  if (semanticType === 'string') return 'text'
  return 'decimal'
}

function simpleControl(semanticType: SemanticType): string {
  if (semanticType === 'money' || semanticType === 'moneyPence') return 'money'
  if (semanticType === 'percentage' || semanticType === 'ratio') return 'percentage'
  if (semanticType === 'boolean') return 'toggle'
  if (semanticType === 'enum') return 'select'
  if (semanticType === 'date') return 'date'
  if (semanticType === 'string') return 'readOnly'
  return 'number'
}

function emitSemanticType(base: unknown, semanticType: SemanticType): JsonRecord {
  const record = asRecord(base)
  const previousKind = record ? asString(record.kind) : null
  if (record && previousKind === semanticType) return { ...record }
  const nullable = record && typeof record.nullable === 'boolean' ? record.nullable : false
  if (record && previousKind !== null && semanticType === 'enum' && Array.isArray(record.enumValues)) {
    return { kind: semanticType, nullable, enumValues: record.enumValues }
  }
  return { kind: semanticType, nullable }
}

/** Project the composer row model back onto a stored source document. */
export function modelToDocument(model: ComposerModel, baseDocument: unknown, options?: ModelToDocumentOptions): JsonRecord {
  return convertModelToDocument(model, baseDocument, options).document
}

export function convertModelToDocument(model: ComposerModel, baseDocument: unknown, options: ModelToDocumentOptions = {}): ModelToDocumentResult {
  const base = asRecord(baseDocument)
  if (!base) throw new TypeError('A base Deal Model source document is required to project the composer model')
  const warnings: ConversionWarning[] = []
  const newId = options.newId ?? randomUuid
  const version = documentVersionOf(base)
  // Bare keys in the model become engine paths in the document, and a fact
  // path written straight into a formula binds to an input: the author's own
  // alias when they declared one, otherwise a synthesised `sys_…` entry.
  const scopeOf = modelScopeResolver(model)
  const aliasOfFactPath = new Map<string, string>()
  for (const row of model.rows) {
    if (row.kind === 'system' && !aliasOfFactPath.has(row.factPath)) aliasOfFactPath.set(row.factPath, row.key)
  }
  const implicit = implicitBindings(model)
  const implicitByPath = new Map(implicit.map((binding) => [binding.factPath, binding]))
  const bindFactPath: FactPathBinder = (factPath) => {
    const alias = aliasOfFactPath.get(factPath)
    if (alias !== undefined) return alias
    return implicitByPath.get(factPath)?.key ?? null
  }

  const baseInputs = asRecordArray(base.inputs)
  const baseRows = asRecordArray(base.rows)
  const baseGroups = asRecordArray(base.groups)
  const baseOutputs = asRecord(base.outputs) ?? { resultPaths: [], kpiRowIds: [], card: { headlinePath: null, secondaryPaths: [], verdictPath: null } }

  /**
   * The stored record this row continues — by identifier, then by key.
   *
   * A base record answers exactly one row. Two rows reaching for the same one
   * (the model carries a stale identifier, say) would otherwise both be written
   * under its id, and the compiler answers `source authoring ID occurs more
   * than once` with a card outcome resolving to whichever row came first.
   */
  const claimedBase = new Set<JsonRecord>()
  const matchBase = (records: JsonRecord[], row: SemanticRow): JsonRecord | null => {
    for (const record of records) if (asString(record.id) === row.id && !claimedBase.has(record)) { claimedBase.add(record); return record }
    for (const record of records) if (asString(record.key) === row.key && !claimedBase.has(record)) { claimedBase.add(record); return record }
    return null
  }

  const documentIds = new Map<string, string>()
  /** Authoring identifiers already written; a document may never repeat one. */
  const emittedIds = new Set<string>()
  const resolveDocumentId = (row: SemanticRow, previous: JsonRecord | null): string => {
    const existing = previous ? asString(previous.id) : null
    const candidate = existing ?? row.id
    let id = isDocumentUuid(candidate) ? candidate : newId()
    if (emittedIds.has(id)) {
      // Unreachable from a model the parser built (reconciliation hands out one
      // identifier per row) — this is the write boundary refusing to store a
      // document the compiler would reject outright.
      warnings.push({ code: 'duplicateDocumentId', message: `Two rows claim the authoring identifier ${id}; ${row.key} was given a new one.`, rowId: row.id })
      id = newId()
    }
    emittedIds.add(id)
    documentIds.set(row.id, id)
    return id
  }

  const checkKey = (row: SemanticRow): void => {
    if (!isDocumentKey(row.key)) {
      warnings.push({ code: 'keyNotDocumentSafe', message: `The key ${row.key} is not a canonical document key and will be rejected on save.`, rowId: row.id })
    }
  }

  const inputs: JsonRecord[] = []
  const rows: JsonRecord[] = []
  const cardOutcomes: OutcomeRow[] = []
  let mainOutcome: OutcomeRow | null = null

  for (const row of model.rows) {
    if (!isSemanticRow(row)) continue
    if (row.kind === 'outcome') {
      if (row.role === 'main') {
        if (mainOutcome !== null) warnings.push({ code: 'multipleMainOutcomes', message: 'Only one main outcome is allowed; later ones are dropped.', rowId: row.id })
        else mainOutcome = row
      } else if (cardOutcomes.length < MAX_CARD_OUTCOMES) cardOutcomes.push(row)
      else warnings.push({ code: 'cardCountExceeded', message: 'Only two card outcomes are allowed; later ones are dropped.', rowId: row.id })
      continue
    }
    checkKey(row)
    if (row.kind === 'calc') {
      const previous = matchBase(baseRows, row)
      const id = resolveDocumentId(row, previous)
      const outputPath = `outputs.${row.key}`
      if (!isDocumentPath(outputPath)) warnings.push({ code: 'pathNotDocumentSafe', message: `The output path ${outputPath} is not a canonical document path.`, rowId: row.id })
      const record: JsonRecord = {
        ...(previous ?? {}),
        id,
        key: row.key,
        label: row.label.length > 0 ? row.label : row.key,
        description: describe(row, previous),
        outputPath,
        semanticType: emitSemanticType(previous?.semanticType, row.semanticType),
        expressionZen: expressionToEnginePaths(row.expressionZen, scopeOf, bindFactPath),
        display: asRecord(previous?.display) ?? { simple: true, formulas: true, result: true, format: displayFormat(row.semanticType) },
      }
      emitUnit(record, row)
      rows.push(record)
      continue
    }
    const previous = matchBase(baseInputs, row)
    const id = resolveDocumentId(row, previous)
    const targetPath = `inputs.${row.key}`
    if (!isDocumentPath(targetPath)) warnings.push({ code: 'pathNotDocumentSafe', message: `The input path ${targetPath} is not a canonical document path.`, rowId: row.id })
    const previousResolution = asRecord(previous?.resolution) ?? {}
    const resolution: JsonRecord = {}
    if (row.kind === 'system') {
      resolution.propertyOverrideKey = asString(previousResolution.propertyOverrideKey) ?? row.key
      resolution.factPath = row.factPath
      resolution.factUnit = factUnitFor(row, previousResolution)
    } else {
      // The current shape is { userSettingKey, propertyOverrideKey } plus the
      // raw-scalar defaultValue. Wave 5c §1: BOTH keys are emitted, so a value
      // can be overridden per user AND per property — without the property key
      // the server refuses the override outright ("input does not permit a
      // property override"). An existing spelling always wins, so a rename
      // never breaks a stored setting or a stored per-property override.
      // An existing property-only value must stay property-only: a personal
      // return target must never become a model-wide default after a text edit.
      if (previous === null || typeof previousResolution.userSettingKey === 'string') {
        resolution.userSettingKey = asString(previousResolution.userSettingKey) ?? row.key
      }
      resolution.propertyOverrideKey = asString(previousResolution.propertyOverrideKey) ?? row.key
    }
    if (Object.keys(resolution).length === 0) resolution.propertyOverrideKey = row.key
    // Preserve the stored property order for an unchanged resolution. The text
    // view must not manufacture a draft edit solely by reordering these keys.
    const resolutionKeys = [...Object.keys(previousResolution).filter(key => key in resolution), ...Object.keys(resolution).filter(key => !(key in previousResolution))]
    const record: JsonRecord = {
      ...(previous ?? {}),
      id,
      key: row.key,
      label: row.label.length > 0 ? row.label : row.key,
      description: describe(row, previous),
      targetPath,
      semanticType: emitSemanticType(previous?.semanticType, row.semanticType),
      requiredForComputation: row.kind === 'input' && row.defaultValue === null
        ? true : typeof previous?.requiredForComputation === 'boolean' ? previous.requiredForComputation : true,
      missingReasonCode: asString(previous?.missingReasonCode) ?? `${row.key}Missing`,
      resolution: Object.fromEntries(resolutionKeys.map(key => [key, resolution[key]])),
      simpleIntent: asRecord(previous?.simpleIntent) ?? { control: simpleControl(row.semanticType), editable: true },
    }
    if (row.kind === 'input' && row.defaultValue !== null) record.defaultValue = row.defaultValue
    else delete record.defaultValue
    emitUnit(record, row)
    inputs.push(record)
  }

  // Every fact a formula named directly becomes an ordinary input entry under
  // its deterministic `sys_…` key. Nothing else creates these, and nothing else
  // keeps them alive: an entry the base carried whose path is no longer
  // referenced simply is not emitted, which is the garbage collection wave 5b
  // §3 asks for.
  // Two distinct paths that still encode to the same `sys_` key would silently
  // become one entry. Say so against a row rather than letting the compiler
  // answer DUPLICATE_SOURCE_KEY with nothing to click on (wave 5c §1).
  for (const collision of implicitBindingKeyCollisions(model)) {
    warnings.push({
      code: 'implicitBindingKeyCollision',
      severity: 'error',
      message: `${collision.factPaths.join(' and ')} both map to the input key ${collision.key}. Alias one of them to a row of its own.`,
      rowId: collision.rowId,
    })
  }
  const writtenImplicitKeys = new Set<string>()
  for (const binding of implicit) {
    if (writtenImplicitKeys.has(binding.key)) continue
    writtenImplicitKeys.add(binding.key)
    const previous = baseInputs.find((record) => asString(record.key) === binding.key) ?? null
    const existingId = previous ? asString(previous.id) : null
    const id = existingId !== null && isDocumentUuid(existingId) && !emittedIds.has(existingId) ? existingId : newId()
    emittedIds.add(id)
    const previousResolution = asRecord(previous?.resolution) ?? {}
    const resolution: JsonRecord = {
      propertyOverrideKey: asString(previousResolution.propertyOverrideKey) ?? binding.key,
      factPath: binding.factPath,
      factUnit: asString(previousResolution.factUnit) ?? binding.factUnit,
    }
    const record: JsonRecord = {
      ...(previous ?? {}),
      id,
      key: binding.key,
      label: asString(previous?.label) ?? binding.factPath,
      description: asString(previous?.description) ?? `Referenced directly as ${binding.factPath}.`,
      targetPath: `inputs.${binding.key}`,
      semanticType: emitSemanticType(previous?.semanticType, binding.semanticType),
      requiredForComputation: typeof previous?.requiredForComputation === 'boolean' ? previous.requiredForComputation : true,
      missingReasonCode: asString(previous?.missingReasonCode) ?? `${binding.key}Missing`,
      resolution,
      simpleIntent: asRecord(previous?.simpleIntent) ?? { control: simpleControl(binding.semanticType), editable: false },
    }
    delete record.defaultValue
    // Wave 5d §1: every implicit key is checked against the schema pattern at
    // write time. The encoding keeps real catalogue paths storable, but a path
    // shape it cannot encode (a segment that starts with an upper-case letter,
    // say) must fail loudly, against the row that names it — never as a silent
    // write the server rejects later.
    if (!isDocumentKey(binding.key)) {
      warnings.push({
        code: 'implicitBindingKeyUnusable',
        severity: 'error',
        message: `${binding.factPath} cannot be bound implicitly: the key ${binding.key} is not a canonical document key. Alias it to a row of its own.`,
        rowId: binding.rowIds[0],
      })
    }
    insertLikeBase(inputs, record, baseInputs)
  }

  // The model orders calc rows for display (grouped rows first); the document
  // orders them for evaluation. Rows the base already carried keep the base's
  // order so a round-trip never reshuffles a dependency chain.
  orderLikeBase(rows, baseRows)
  orderLikeBase(inputs, baseInputs)

  const rowIdSet = new Set(rows.map((record) => String(record.id)))
  const inputIdSet = new Set(inputs.map((record) => String(record.id)))

  const groups: JsonRecord[] = model.groups.map((group, index) => {
    const previous = baseGroups.find((record) => asString(record.id) === group.id) ?? null
    const preserved = previous
      ? asRecordArray(previous.items).filter((item) => asString(item.kind) !== 'row' && asString(item.kind) !== 'input')
      : []
    const previousInputIds = new Set(previous ? asRecordArray(previous.items).filter(item => item.kind === 'input').map(item => asString(item.id)) : [])
    const items: JsonRecord[] = [...preserved]
    for (const rowId of group.rowIds) {
      const documentId = documentIds.get(rowId) ?? rowId
      if (rowIdSet.has(documentId)) items.push({ kind: 'row', id: documentId })
      else if (inputIdSet.has(documentId)) {
        if (options.inputGroupItems === true || previousInputIds.has(documentId)) items.push({ kind: 'input', id: documentId })
        else warnings.push({ code: 'inputGroupItemDropped', message: `Group ${group.label} lists an input row; the document schema only stores row and blockInstance items.`, rowId })
      }
    }
    const groupId = isDocumentUuid(group.id) ? group.id : (previous ? String(previous.id) : newId())
    const label = group.label.length > 0 ? group.label : `Group ${index + 1}`
    return {
      ...(previous ?? {}),
      id: groupId,
      key: asString(previous?.key) ?? groupKeyFromLabel(label, index),
      label,
      description: asString(previous?.description) ?? label,
      items,
    }
  })

  const document: JsonRecord = { ...base, inputs, groups, rows }
  document.contractVersion = CONTRACT_VERSION_V02

  // `composerAnnotations` is optional and the compiler ignores it, so it is
  // written whenever there is something to write — and NOT written onto a
  // document that never had it and has nothing to say, which is what keeps a
  // save-without-editing byte-identical to what was loaded.
  const collected = collectComposerAnnotations(model, documentIds, newId)
  // The contract types a comment's text as a non-blank single line, so a bare
  // `#` on its own has nothing to store. It survives the session in the text
  // view and is reported rather than written as an empty comment the server
  // would refuse.
  const annotations = collected.filter((annotation) => annotation.kind === 'blank' || (annotation.text ?? '').trim().length > 0)
  const emptyComments = collected.length - annotations.length
  if (emptyComments > 0) {
    warnings.push({
      code: 'composerAnnotationsDropped',
      message: `${emptyComments} empty comment line(s) are not stored: a stored comment carries text.`,
    })
  }
  const annotationsWanted = options.composerAnnotations ?? true
  const carriesAnnotations = documentCarriesComposerAnnotations(base)
  if (annotationsWanted && (annotations.length > 0 || carriesAnnotations)) {
    if (annotations.length > MAX_COMPOSER_ANNOTATIONS) {
      warnings.push({
        code: 'composerAnnotationsTruncated',
        message: `A document stores at most ${MAX_COMPOSER_ANNOTATIONS} comment or blank lines; ${annotations.length - MAX_COMPOSER_ANNOTATIONS} beyond that are not saved.`,
      })
    }
    document[COMPOSER_ANNOTATIONS_KEY] = annotations.slice(0, MAX_COMPOSER_ANNOTATIONS).map((annotation) => ({
      id: annotation.id,
      kind: annotation.kind,
      afterRowId: annotation.afterRowId,
      ...(annotation.kind === 'comment' ? { text: annotation.text } : {}),
    }))
  } else {
    delete document[COMPOSER_ANNOTATIONS_KEY]
    if (annotations.length > 0) {
      warnings.push({
        code: 'composerAnnotationsDropped',
        message: `${annotations.length} comment or blank line(s) are not stored, because this document does not carry ${COMPOSER_ANNOTATIONS_KEY}.`,
      })
    }
  }

  // `outputs.resultPaths` is not a set the composer may order as it likes: the
  // contract requires the exact DECLARED executable result-path set, in
  // declaration order — every row's outputPath, then each check's status and
  // explanation path, then the main outcome, then each solver objective's result
  // path. Appending `deal.<key>` at the end produces a document the server
  // refuses with TYPED_REFERENCE_INVALID when a model also declares a solver.
  const resultPaths = new Set<string>()
  for (const record of rows) resultPaths.add(String(record.outputPath))
  const mainOutputPath = mainOutcome === null ? null : `deal.${mainOutcome.key}`
  const beforeVerdict: string[] = []
  const afterVerdict: string[] = []
  for (const path of Array.isArray(baseOutputs.resultPaths) ? baseOutputs.resultPaths : []) {
    const value = asString(path)
    if (value === null || value.startsWith('outputs.')) continue
    // `deal.<key>` is re-derived from the model.
    if (value.startsWith('deal.')) continue
    // The preserved objective declarations are the authority for solver paths.
    // A standalone sheet that explicitly removes them must not inherit a path
    // from the base model's prior output selection.
    if (value.startsWith('solverResults.')) continue
    if (value.startsWith('checks.') || value.startsWith('checkDetails.')) beforeVerdict.push(value)
    else afterVerdict.push(value)
  }
  for (const value of beforeVerdict) resultPaths.add(value)

  // Card values are KPIs, but the document's KPI list is broader than the card
  // and the composer does not author it — preserve what is still resolvable.
  const kpiRowIds: string[] = []
  const rowIdsInOrder = new Set(rows.map((record) => String(record.id)))
  for (const id of Array.isArray(baseOutputs.kpiRowIds) ? baseOutputs.kpiRowIds : []) {
    const value = asString(id)
    if (value !== null && rowIdsInOrder.has(value) && !kpiRowIds.includes(value)) kpiRowIds.push(value)
  }
  const cardEntries: JsonRecord[] = []
  for (const outcome of cardOutcomes) {
    const sourceKey = outcome.sourceRowKey ?? outcome.key
    const referenced = rows.find((record) => asString(record.key) === sourceKey)
    if (!referenced) {
      warnings.push({ code: 'cardSourceUnresolved', message: `The card outcome ${outcome.key} does not name an existing calculated row.`, rowId: outcome.id })
      continue
    }
    const referencedId = String(referenced.id)
    if (!kpiRowIds.includes(referencedId)) kpiRowIds.push(referencedId)
    if (outcome.key !== String(referenced.key)) {
      warnings.push({
        code: 'cardKeyRenamed',
        message: `The card ${outcome.key} exposes the row ${String(referenced.key)}; the document stores the row's own key.`,
        rowId: outcome.id,
      })
    }
    // v0.2 card entries carry the referenced calc row verbatim.
    cardEntries.push({
      rowId: referencedId,
      key: String(referenced.key),
      // An unnamed card (`card profit`) inherits the row's own label.
      label: outcome.label.length > 0 && outcome.label !== outcome.key ? outcome.label : String(referenced.label),
      sourcePath: String(referenced.outputPath),
    })
  }

  // The current source format has no `verdict` or `outputs.card`.
  delete document.verdict
  const tiers = mainOutcome === null ? [] : normaliseTiers(mainOutcome, warnings)
  if (mainOutcome !== null && tiers.length === 0) {
    warnings.push({ code: 'mainOutcomeDropped', message: `The main outcome ${mainOutcome.key} has no tiers and cannot be stored.`, rowId: mainOutcome.id })
  }
  if (mainOutcome !== null && tiers.length > 0) {
    const baseOutcomes = asRecord(base.outcomes)
    const baseMain = baseOutcomes === null ? null : asRecord(baseOutcomes.main)
    const baseTiers = baseMain === null ? [] : asRecordArray(baseMain.tiers)
    const usedTierIds = new Set<string>()
    const tierId = (value: string, index: number): string => {
      const candidates = [
        ...baseTiers.filter((tier) => asString(tier.value) === value),
        ...(baseTiers[index] === undefined ? [] : [baseTiers[index] as JsonRecord]),
      ]
      for (const candidate of candidates) {
        const id = asString(candidate.id)
        if (id !== null && isDocumentUuid(id) && !usedTierIds.has(id)) { usedTierIds.add(id); return id }
      }
      const minted = newId()
      usedTierIds.add(minted)
      return minted
    }
    const mainRowId = isDocumentUuid(mainOutcome.id) ? mainOutcome.id : newId()
    document.outcomes = {
      main: {
        rowId: mainRowId,
        key: mainOutcome.key,
        label: mainOutcome.label.length > 0 ? mainOutcome.label : 'Verdict',
        outputPath: `deal.${mainOutcome.key}`,
        semanticType: { kind: 'enum', nullable: false, enumValues: [...new Set(tiers.map((tier) => tier.value))] },
        tiers: tiers.map((tier, index) => ({
          id: tierId(tier.value, index),
          value: tier.value,
          conditionZen: tier.conditionZen === null ? null : expressionToEnginePaths(tier.conditionZen, scopeOf, bindFactPath),
        })),
      },
      card: cardEntries,
    }
    if (mainOutputPath !== null) resultPaths.add(mainOutputPath)
    document.verdictContract = 'declared'
  } else {
    document.outcomes = { main: null, card: cardEntries }
    document.verdictContract = 'none'
  }

  // Everything the base declared after its verdict — solver objectives, and any
  // scenario path a future contract adds — closes the list.
  for (const value of afterVerdict) resultPaths.add(value)
  for (const objective of Array.isArray(base.solverObjectives) ? base.solverObjectives : []) {
    const path = asString(asRecord(objective)?.resultPath)
    if (path !== null) resultPaths.add(path)
  }

  document.outputs = { resultPaths: [...resultPaths], kpiRowIds }
  return { document, version, warnings }
}

/**
 * Write the row's display unit onto the record it is emitted as.
 *
 * The record is built by spreading the base document's entry, so a unit the
 * author has just CLEARED is still sitting on it. Only ever adding the unit back
 * therefore left the grid showing an empty box over a document that still
 * declared `"unit": "gbp"` — the clear was accepted in the UI and silently
 * ignored on save. An absent unit is now deleted rather than inherited.
 */
function emitUnit(record: JsonRecord, row: SemanticRow): void {
  if (row.unit !== undefined) {
    record.unit = row.unit
    return
  }
  delete record.unit
}

/**
 * Validate the tiers as the current source document will store them. Tiers are
 * never dropped: losing an authored rule would silently change the model.
 */
function normaliseTiers(outcome: OutcomeRow, warnings: ConversionWarning[]): OutcomeTier[] {
  const tiers = outcome.tiers ?? []
  if (tiers.length === 0) warnings.push({ code: 'tiersMissing', message: `The main outcome ${outcome.key} declares no tiers.`, rowId: outcome.id })
  if (tiers.length > MAX_OUTCOME_TIERS) warnings.push({ code: 'tierCountExceeded', message: `The main outcome ${outcome.key} declares ${tiers.length} tiers; only ${MAX_OUTCOME_TIERS} are allowed.`, rowId: outcome.id })
  tiers.forEach((tier, index) => {
    if (isReservedTierValue(tier.value)) {
      warnings.push({ code: 'reservedTierValue', message: `${tier.value} is reserved for the compiler and cannot be an authored tier value.`, rowId: outcome.id })
    }
    if (tier.conditionZen === null && index !== tiers.length - 1) {
      warnings.push({ code: 'defaultTierNotLast', message: `Only the last tier of ${outcome.key} may be a default.`, rowId: outcome.id })
    }
  })
  return tiers
}

function groupKeyFromLabel(label: string, index: number): string {
  const words = label.toLowerCase().replace(/[^a-z0-9]+/gu, ' ').trim().split(/\s+/u).filter((word) => word.length > 0)
  if (words.length === 0) return `group${index + 1}`
  const head = words[0] as string
  const candidate = head + words.slice(1).map((word) => word[0]?.toUpperCase() + word.slice(1)).join('')
  const cleaned = candidate.replace(/^[^a-z]+/u, '')
  return isDocumentKey(cleaned) ? cleaned : `group${index + 1}`
}

/**
 * Put a synthesised record where the base document already kept it.
 *
 * An implicit binding is not a row of the sheet, so it has no position of its
 * own: appending it would move the entry every time the base carried it
 * somewhere else, and a save that changed nothing would rewrite the file. The
 * record therefore lands immediately before the first already-emitted entry the
 * base ordered after it, and at the end when the base never held it.
 */
function insertLikeBase(records: JsonRecord[], record: JsonRecord, baseRecords: JsonRecord[]): void {
  const position = new Map<string, number>()
  baseRecords.forEach((entry, index) => {
    const key = asString(entry.key)
    if (key !== null && !position.has(key)) position.set(key, index)
  })
  const own = position.get(String(record.key))
  if (own !== undefined) {
    for (let index = 0; index < records.length; index += 1) {
      const other = position.get(String((records[index] as JsonRecord).key))
      if (other !== undefined && other > own) { records.splice(index, 0, record); return }
    }
  }
  records.push(record)
}

/**
 * Reorder `records` in place so that everything the base document already held
 * keeps the base's relative order, and anything new stays immediately after the
 * record it followed in the model. A model built from scratch is unaffected.
 */
function orderLikeBase(records: JsonRecord[], baseRecords: JsonRecord[]): void {
  if (records.length < 2 || baseRecords.length === 0) return
  const basePosition = new Map<string, number>()
  baseRecords.forEach((record, index) => {
    const id = asString(record.id)
    if (id !== null && !basePosition.has(id)) basePosition.set(id, index)
  })
  const known = records.filter((record) => basePosition.has(String(record.id)))
  if (known.length === 0) return
  const ordered = [...known].sort((left, right) => (basePosition.get(String(left.id)) as number) - (basePosition.get(String(right.id)) as number))
  // Re-insert the fresh records after whichever known record preceded them.
  let anchor: JsonRecord | null = null
  const trailing = new Map<JsonRecord | null, JsonRecord[]>()
  for (const record of records) {
    if (basePosition.has(String(record.id))) { anchor = record; continue }
    const bucket = trailing.get(anchor) ?? []
    bucket.push(record)
    trailing.set(anchor, bucket)
  }
  const out: JsonRecord[] = [...(trailing.get(null) ?? [])]
  for (const record of ordered) {
    out.push(record)
    for (const fresh of trailing.get(record) ?? []) out.push(fresh)
  }
  records.splice(0, records.length, ...out)
}

/**
 * The document requires a non-empty description on every input. The row note
 * wins, then whatever the base document already said, then the label.
 */
function describe(row: SemanticRow, previous: JsonRecord | null): string {
  if (row.note !== undefined) {
    const note = singleLine(row.note)
    if (note.length > 0) return note
  }
  const prior = previous === null ? null : previous.description
  if (typeof prior === 'string' && prior.length > 0) return prior
  return row.label.length > 0 ? row.label : row.key
}

/**
 * v0.2 declares the unit a bound fact arrives in, so the compiler can emit the
 * ÷100 at binding. An explicit unit on the base document always wins.
 */
function factUnitFor(row: SystemRow, previousResolution: JsonRecord): string {
  const declared = asString(previousResolution.factUnit)
  if (declared !== null) return declared
  if (/Pence$/u.test(row.factPath)) return 'pence'
  return row.semanticType === 'money' ? 'pounds' : 'native'
}
